import { Component } from 'react';
import styles from '../../styles/home/homeSearch.scss';
import cx from 'classnames';
import { Router } from '../../configs/routes';
import { parse } from 'query-string';
import { Api } from '../../api';
import { CDN_CLRSAVE_URL } from '../../constants';
import { VARIANT_VALUES } from '../../constants/variantConstants';
import { handleTrack } from '../../utils/trackActions';
import getConfig from 'next/config';
import {
  algoliaIndex,
  stagingAlgoliaIndex,
} from '../../constants/searchConstants';
const { publicRuntimeConfig } = getConfig();

const api = new Api();

const ENTER_KEY = 13;
const UP_ARROW = 38;
const DOWN_ARROW = 40;

interface Props {
  fullWidth?: boolean;
  placeholder?: string;
  mobileNav?: boolean;
  afterSearch?: () => void;
  handleFundClick?: (e: any) => void;
  hideSearchLabel?: boolean;
  variant?: string;
}

interface State {
  results: any[];
  totalResults: number;
  query: string;
  currentSelectedResult: number;
  placeholder: string;
}

class HomeSearch extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { placeholder } = props;
    this.state = {
      results: [],
      totalResults: 0,
      query: '',
      currentSelectedResult: -1,
      placeholder:
        placeholder || 'Find a mutual fund by name, type or fund house',
    };
  }

  public handleKeyDown = (e: any) => {
    const { currentSelectedResult, results, query } = this.state;
    const { hideSearchLabel } = this.props;
    if (e.keyCode === ENTER_KEY) {
      if (currentSelectedResult !== -1) {
        const selectedItem = results[currentSelectedResult];
        if (selectedItem) {
          const {
            isinCode,
            fundMetaData,
            fundName,
            planType = 'GROWTH',
          } = selectedItem;
          const { parentSlug, slug } = fundMetaData;
          if (hideSearchLabel) {
            this.props.handleFundClick(`${isinCode}`);
            this.resetState();
          } else {
            const path = `/mutual-funds/${parentSlug}/${slug}/${isinCode}?planType=${planType}`;
            handleTrack({
              action: 'Enter',
              label: `search_input_result_${fundName}`,
              value: `{}search_query :: ${query}`,
              url: path,
            });
            Router.pushRoute(path);
          }
        }
      } else if (!hideSearchLabel) {
        this.navigateToSearch(e);
      }
    }

    const totalShownItems = results.length;
    const isLastItem = currentSelectedResult === totalShownItems - 1;
    const isFirstItem = currentSelectedResult === 0;
    if (e.keyCode === DOWN_ARROW) {
      this.setState({
        currentSelectedResult: isLastItem ? 0 : currentSelectedResult + 1,
      });
    } else if (e.keyCode === UP_ARROW) {
      this.setState({
        currentSelectedResult: isFirstItem
          ? totalShownItems - 1
          : currentSelectedResult - 1,
      });
    }
  };

  public navigateToFund = (e: any) => {
    const slug = e.target.dataset.slug || e.currentTarget.dataset.slug;
    const productID = e.target.dataset.pid || e.currentTarget.dataset.pid;
    const { query } = this.state;
    const planType =
      e.target.dataset.plantype || e.currentTarget.dataset.plantype || 'GROWTH';
    let route = `/mutual-funds/${slug}?planType=${planType}`;
    if (productID == null) {
      route = `/mutual-funds/${slug}?type=non_onboarded&planType=${planType}`;
    }
    handleTrack({
      label: 'search_input_result',
      value: `{}search_query :: ${query}`,
      url: `${route}`,
      obj: e.target,
    });
    Router.pushRoute(route);
  };

  private handleFundClick = (e: any) => {
    const { handleFundClick } = this.props;
    this.resetState();
    if (handleFundClick) {
      handleFundClick(e);
    } else {
      this.navigateToFund(e);
    }
  };

  private resetState() {
    this.setState({
      query: '',
      results: [],
      totalResults: 0,
      currentSelectedResult: -1,
    });
  }

  public navigateToSearch = (e: any) => {
    const { afterSearch } = this.props;
    const filters = JSON.stringify({
      query: this.ref.value,
    });
    const queries = parse(location.search);
    queries.filters = filters;
    if (afterSearch && typeof afterSearch === 'function') {
      afterSearch();
    }
    handleTrack({
      label: 'search_input_button',
      value: `{}search_query :: ${this.ref.value}`,
      obj: e.target,
    });
    Router.pushRoute('search', queries);
  };

  public componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
    if (window.innerWidth < 1024) {
      this.setState({
        placeholder: 'Find a mutual fund...',
      });
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  public handleDocumentClick = (e: any) => {
    if (e.target.id !== 'navbar-search') {
      this.setState({
        totalResults: 0,
      });
    }
  };

  public handleChange = (e: any) => {
    const { value } = e.target;
    this.searchFund(value);
  };

  public searchFund = (value?: string) => {
    this.setState({
      query: value,
    });
    if (!value) {
      return;
    }
    const index =
      publicRuntimeConfig.env === 'development' ||
      publicRuntimeConfig.env === 'qa'
        ? stagingAlgoliaIndex
        : algoliaIndex;
    api
      .searchFund(
        value,
        0,
        [['purchaseMode:DIRECT'], ['enabled:true'], [], [], []],
        index
      )
      .then((res) => {
        this.setState({
          results: res.hits.slice(0, 10),
          totalResults: res.nbHits,
        });
      });
  };

  public handleBlur = () => {
    const { query } = this.state;
    this.searchFund(query);
  };

  public handleItemHover = (i: number) => {
    this.setState({
      currentSelectedResult: i,
    });
  };

  public render() {
    const { fullWidth, hideSearchLabel, variant } = this.props;
    const { results, totalResults, query, currentSelectedResult, placeholder } =
      this.state;
    return (
      <div
        className={cx('relative', styles['search-container'], {
          'half-width': !fullWidth,
          [styles['size-large']]: variant === VARIANT_VALUES.B,
        })}
      >
        <input
          placeholder={placeholder}
          id="navbar-search"
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          onFocus={this.handleBlur}
          value={query}
          className={cx(
            styles['search-input'],
            'font--normal text--xmd font--dark-gray'
          )}
          ref={(ref) => {
            this.ref = ref;
          }}
        />
        {!hideSearchLabel ? (
          <span
            className={cx(styles['search-help'], 'text--md font--bold', {
              's-btn s-btn--blue': variant === VARIANT_VALUES.B,
            })}
            onClick={this.navigateToSearch}
          >
            Search{' '}
          </span>
        ) : null}

        {totalResults && query ? (
          <div className={cx(styles['autocomplete-results'])}>
            <ul>
              {results.map((result, i) => {
                const {
                  fundName,
                  imageUrl,
                  fundMetaData,
                  isinCode,
                  productId,
                  planType,
                } = result;
                const { parentSlug, slug } = fundMetaData;
                return (
                  <li
                    key={isinCode}
                    onClick={this.handleFundClick}
                    data-slug={`${parentSlug}/${slug}/${isinCode}`}
                    onMouseEnter={() => this.handleItemHover(i)}
                    data-productid={`${isinCode}`}
                    data-pid={productId}
                    data-plantype={planType}
                    className={cx({
                      [styles['selected']]: i === currentSelectedResult,
                    })}
                  >
                    <img src={`${CDN_CLRSAVE_URL}/clearsave${imageUrl}`} />
                    <span>{fundName}</span>
                  </li>
                );
              })}
              {!hideSearchLabel && totalResults > 10 ? (
                <li onClick={this.navigateToSearch} className="text--md">
                  <a href="#">Show all results for {query}</a>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
}

export default HomeSearch;



// WEBPACK FOOTER //
// ./components/home/homeSearch.tsx